import { type OnboardingEvent, OnboardingState, Tier } from '@developerz/domain';
import { IconArrowRight } from '@developerz/ui';
import { A } from '@solidjs/router';
import { type JSX, Match, Switch } from 'solid-js';
import { t } from '../i18n';
import type { IntendedTier } from '../lib/onboarding';
import { ONBOARDING_STEPS } from '../lib/onboardingSteps';
import { orgHref } from '../lib/org';
import { OnboardingAdvanceButton } from './OnboardingAdvanceButton';
import { buttonClass } from './ui/Button';

// The funnel panel's action row. Presenter — one branch per state, and EVERY
// state has one. The step's forward action is the view's ONE `brand` button
// (owner ruling 2026-09-16, `btn--brand`); every other control is `outline`. The states that used to have none (`new`, `yml_requested`) are
// why `OnboardingPanel.states.test.tsx` walks the enum.
//
// The event each button fires comes from `ONBOARDING_STEPS`, never a literal
// per branch: the panel used to hardcode the transition beside a `next_move`
// the server was already computing, which is two copies of one transition table.
export type OnboardingPanelFooterProps = {
  readonly state: OnboardingState;
  /** The card is ahead of the stored ladder: its advance would 409, so Continue re-reads. */
  readonly displayOnly: boolean;
  readonly busy: boolean;
  /** No repo is enabled and the walk LANDED — Continue would advance past nothing. */
  readonly reposBlocked: boolean;
  readonly ghAppInstallUrl: string;
  readonly onAdvance: (event: OnboardingEvent, intendedTier?: IntendedTier) => void;
  readonly onRecheck: () => void;
};

export function OnboardingPanelFooter(props: OnboardingPanelFooterProps): JSX.Element {
  const spec = () => ONBOARDING_STEPS[props.state];
  const label = (): string => t(spec().actionKey);
  const advance = (tier?: IntendedTier) => (): void => {
    const event = spec().advanceEvent;
    if (props.displayOnly) props.onRecheck();
    else if (event !== null) props.onAdvance(event, tier);
  };
  return (
    <div class="onboarding-step__actions">
      <Switch>
        <Match when={props.state === OnboardingState.New}>
          <OnboardingAdvanceButton label={label()} busy={props.busy} onAct={advance()} />
        </Match>
        <Match when={props.state === OnboardingState.Authed}>
          <OnboardingAdvanceButton
            label={t('onboarding.plan_single_cta')}
            busy={props.busy}
            secondary
            onAct={advance(Tier.Solo)}
          />
          <OnboardingAdvanceButton label={label()} busy={props.busy} onAct={advance(Tier.Pro)} />
        </Match>
        <Match when={props.state === OnboardingState.PlanSelected}>
          <A class={buttonClass({ variant: 'brand' })} href={orgHref('/account/keys')}>
            {label()}
            <IconArrowRight size="0.875rem" />
          </A>
        </Match>
        <Match when={props.state === OnboardingState.KeySet}>
          {/* NOT target="_blank": `apps/api/src/routes/gh-installed.ts` redirects
              back onto this funnel, and a new tab left that landing in tab B
              while the wizard the user was reading sat in tab A. */}
          <a
            class={buttonClass({ variant: 'brand' })}
            href={props.ghAppInstallUrl}
            rel="noreferrer"
          >
            {label()}
            <IconArrowRight size="0.875rem" />
          </a>
          <A class={buttonClass({ variant: 'outline' })} href={orgHref('/account/github')}>
            {t('onboarding.connect_token_cta')}
          </A>
        </Match>
        {/* No advance: `box_attached` is platform-witnessed (the runner's first
            heartbeat, settled by the API's fact ladder), so the only lever this
            panel has is a re-read. A Continue here would let a caller claim a
            machine that never beat — the funnel's whole progress signal. The way
            OUT is "later", and the line beside it says what skipping costs:
            there is no skip event, so later only leaves, and the sidebar's
            finish-setup CTA brings the account back to this exact step. */}
        <Match when={props.state === OnboardingState.GhConnected}>
          <OnboardingAdvanceButton
            label={label()}
            busy={props.busy}
            onAct={() => props.onRecheck()}
          />
          <A class={buttonClass({ variant: 'outline' })} href={orgHref('/')} end>
            {t('onboarding.box_later_cta')}
          </A>
          <p class="onboarding-step__warning">{t('onboarding.box_later_warning')}</p>
        </Match>
        <Match when={props.state === OnboardingState.BoxAttached}>
          <OnboardingAdvanceButton
            label={label()}
            busy={props.busy}
            blocked={props.reposBlocked}
            onAct={advance()}
          />
        </Match>
        {/* The primary is an ADVANCE now, not a clipboard write: the step's
            content is the pass's `dz/onboarding` pull request (arc 101 slice 05
            step 6), and the prompt's copy button moved beside the prompt itself
            (`OnboardingYmlBlock`). Merging that PR completes the funnel from
            EITHER state, so this button is the maintainer saying "I have it",
            not the thing that makes the step work. */}
        <Match when={props.state === OnboardingState.ReposSelected}>
          <OnboardingAdvanceButton label={label()} busy={props.busy} onAct={advance()} />
          <A class={buttonClass({ variant: 'outline' })} href={orgHref('/repos')}>
            {t('onboarding.repos_manage_cta')}
          </A>
        </Match>
        {/* `mcp_connected` is platform-witnessed too — the connector's own grant
            is the evidence, so this card re-reads and never asserts. */}
        <Match when={props.state === OnboardingState.YmlRequested}>
          <OnboardingAdvanceButton
            label={label()}
            busy={props.busy}
            onAct={() => props.onRecheck()}
          />
          <A class={buttonClass({ variant: 'outline' })} href={orgHref('/account/tokens')}>
            {t('onboarding.mcp_tokens_cta')}
          </A>
        </Match>
        <Match when={props.state === OnboardingState.McpConnected}>
          <OnboardingAdvanceButton
            label={label()}
            busy={props.busy}
            onAct={() => props.onRecheck()}
          />
          <a class={buttonClass({ variant: 'outline' })} href={orgHref('/repos')}>
            {t('onboarding.validate_repos_cta')}
          </a>
        </Match>
        <Match when={props.state === OnboardingState.Invalid}>
          <OnboardingAdvanceButton label={label()} busy={props.busy} onAct={advance()} />
        </Match>
      </Switch>
    </div>
  );
}
